import { daysBetween } from "./member-home.ts";
import { addDays, formatDayKey, shiftDateKey } from "./time.ts";

/**
 * A paused package (epic M3 "Freeze a pack", migration 0071).
 *
 * The owner pauses a package for travel, injury or pregnancy. The package end date moves by the
 * paused days, and a paused package cannot pay for a session in the pause. The rules are here, in
 * one place: the API freezes and unfreezes with them, and Desk web and the phone read the pause
 * with them.
 *
 * The end date is stored on the package (`package_purchases.expires_at`) already moved: the pause
 * moves it forward by its days, and an early end moves it back by the days that were not used. So
 * every reader of the end date (the balance, the booking check, the expiry job, the 7-day warning,
 * the screens) reads one column and respects the pause, and none of them repeats the rule.
 */

/** The fewest days of one pause. */
export const FREEZE_MIN_DAYS = 1;
/** The most days of one pause. A longer absence gets a second pause after the first one. */
export const FREEZE_MAX_DAYS = 90;

/** A pause as the rules read it: the first paused day and the last one, `yyyy-MM-dd` in the business timezone. */
export type PauseDays = { startsOn: string; endsOn: string };

/** The last paused day of a pause of `days` days that starts on `startsOn`. */
export function pauseLastDay(startsOn: string, days: number): string {
  return shiftDateKey(startsOn, days - 1);
}

/** The number of paused days, the first and the last day included. */
export function pauseLength(pause: PauseDays): number {
  return daysBetween(pause.startsOn, pause.endsOn) + 1;
}

/**
 * The days of a pause that were used when it ends early on `endedOn`. `endedOn` is the first day
 * that is not paused (the day the owner ends the pause), so a pause that ends before it starts used
 * no days, and the value is never more than the length of the pause.
 */
export function pauseDaysUsed(pause: PauseDays, endedOn: string): number {
  return Math.max(0, Math.min(pauseLength(pause), daysBetween(pause.startsOn, endedOn)));
}

/**
 * The pause as it is after an early end on `endedOn`, or null when it used no days. The rows keep
 * the planned `ends_on` and the `ended_early_on` day; the screens and the booking check read this.
 */
export function pauseAfterEarlyEnd<T extends PauseDays>(pause: T, endedOn: string | null): T | null {
  if (!endedOn) return pause;
  const used = pauseDaysUsed(pause, endedOn);
  if (used === 0) return null;
  return { ...pause, endsOn: pauseLastDay(pause.startsOn, used) };
}

/** True when the two pauses share one day or more. */
export function pausesOverlap(a: PauseDays, b: PauseDays): boolean {
  return a.startsOn <= b.endsOn && b.startsOn <= a.endsOn;
}

/** True when `day` (`yyyy-MM-dd`) is a paused day of one of the pauses. */
export function pausedOn(pauses: readonly PauseDays[], day: string): boolean {
  return pauses.some((pause) => pause.startsOn <= day && day <= pause.endsOn);
}

/**
 * The pause the screens show: the one in force today, else the next one to start. Null when no
 * pause covers today or a later day.
 */
export function openPause<T extends PauseDays>(pauses: readonly T[], today: string): T | null {
  return [...pauses].filter((pause) => pause.endsOn >= today).sort((a, b) => a.startsOn.localeCompare(b.startsOn))[0] ?? null;
}

/**
 * The one rule that moves a package end date: by whole days, forward for a pause and back (a
 * negative number) for the unused days of a pause that ends early.
 */
export function movePackageEnd(expiresAt: Date, days: number): Date {
  return addDays(expiresAt, days);
}

/** "Paused until 14 Nov 2026" when the pause is in force today, "Paused from 1 Nov 2026 to 14 Nov 2026" before it starts. */
export function pauseLine(pause: PauseDays, today: string): string {
  if (pause.startsOn <= today) return `Paused until ${formatDayKey(pause.endsOn)}`;
  return `Paused from ${formatDayKey(pause.startsOn)} to ${formatDayKey(pause.endsOn)}`;
}

/** The words for each reason, in the order the freeze sheet shows them. */
export const freezeReasonLabels = {
  travel: "Travel",
  injury: "Injury",
  pregnancy: "Pregnancy",
  other: "Other",
} as const;

/**
 * The answer to Freeze all (D17) in words, on Desk web and the phone: "2 packages paused. Not
 * paused: Private 5 (it has no sessions left)." A pack that cannot take the pause is named with why.
 */
export function freezeManyLine(frozen: number, skipped: readonly { packageName: string; reason: string }[]): string {
  const done = `${frozen} ${frozen === 1 ? "package" : "packages"} paused`;
  if (skipped.length === 0) return done;
  return `${done}. Not paused: ${skipped.map((row) => `${row.packageName} (${row.reason.replace(/\.$/, "")})`).join("; ")}.`;
}

/** The answer to End all pauses (D17): "2 pauses ended". */
export function endManyLine(ended: number): string {
  return `${ended} ${ended === 1 ? "pause" : "pauses"} ended`;
}
